import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import { FilterOptionsState } from '@mui/lab/useAutocomplete';
import Autocomplete, {
  AutocompleteRenderInputParams,
} from '@mui/lab/Autocomplete';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { COLORS } from '../settings/variables/Colors';
import { SPACERS } from '../settings/variables/Spacers';
import TrueImpactText from '../base/TrueImpactText';

interface TrueImpactMultiSelectSearchProps<T> {
  options: T[];
  selected: T[];
  className?: string;
  placeholder: string;
  clearAllText?: string;
  compare: (o1: T, o2: T) => boolean;
  onChange: (selected: T[]) => unknown;
  getId: (option: T) => string | number;
  renderOption: (option: T) => React.ReactNode;
  filterOptions: (options: T[], state: FilterOptionsState<T>) => T[];
}

const StyledDiv = styled.div`
  .start-adornment {
    padding-left: 12px;
    padding-right: ${SPACERS.tiny};
  }
  .autocomplete-chips-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: ${SPACERS.tiny};
    margin-top: ${SPACERS.small};
    .autocomplete-chip {
      display: flex;
      align-items: center;
      border-radius: 56px;
      gap: ${SPACERS.extraTiny};
      background-color: ${COLORS.grayLight};
      padding: ${SPACERS.tiny} ${SPACERS.small};
      svg {
        width: 16px;
        height: 16px;
        cursor: pointer;
      }
    }
  }
  .autocomplete-clear-all {
    cursor: pointer;
    margin-top: ${SPACERS.medium};
  }
`;

const TrueImpactMultiSelectSearch = <T,>({
  options,
  selected,
  onChange,
  getId,
  compare,
  renderOption,
  filterOptions,
  clearAllText,
  placeholder,
  className,
}: TrueImpactMultiSelectSearchProps<T>) => {
  const [values, setValues] = useState<T[]>(selected);
  const [text, setText] = useState<T | undefined>(undefined);

  const handleClearAll = useCallback(() => {
    setValues([]);
    setText(undefined);
  }, [setValues, setText]);

  useEffect(() => {
    onChange(values);
  }, [values]);

  return (
    <StyledDiv className={className}>
      <Autocomplete
        value={text}
        placeholder={placeholder}
        freeSolo={true}
        clearOnBlur={false}
        onChange={(_, value) => {
          if (!value) return;
          setText(undefined);
          if (values.findIndex(x => compare(x, value as T)) !== -1) return;
          setValues(s => [...s, value as T]);
        }}
        handleHomeEndKeys={true}
        itemID="autocomplete-item"
        renderOption={renderOption}
        selectOnFocus={true}
        options={options}
        filterOptions={filterOptions}
        renderInput={(
          params: AutocompleteRenderInputParams,
        ): React.ReactNode => (
          <TextField
            {...params}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <div className="start-adornment">
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    color={COLORS.ctaPrimary}
                  />
                </div>
              ),
              endAdornment: null,
            }}
          />
        )}
      />
      <div className="autocomplete-chips-wrapper">
        {values.map(v => (
          <div key={getId(v)} className="autocomplete-chip">
            {renderOption(v)}
            <FontAwesomeIcon
              icon={faClose}
              onClick={() => setValues(s => s.filter(x => !compare(x, v)))}
            />
          </div>
        ))}
      </div>
      {clearAllText && values.length > 0 && (
        <TrueImpactText
          variant="body-small"
          className="autocomplete-clear-all"
          onClick={handleClearAll}
        >
          {clearAllText}
        </TrueImpactText>
      )}
    </StyledDiv>
  );
};

export default TrueImpactMultiSelectSearch;
